import { HttpClient, HttpResponse } from '@angular/common/http';
import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import { ApiService } from '../../services/api/api.service';
import * as saveAs from 'file-saver';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { RegionService } from '../region/region.service';

export interface SurveyRefereeAttachment {
  created_date: number;
  description: string;
  download_url: string;
  title: string;
}

@Injectable()
export class SurveyService {
  surveyUrl = `${environment.SURVEY_URL}`;
  data: any = {};
  dataUpdated$ = new Subject<Object>();

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private region: RegionService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.surveyUrl = this.region.getRegionalUrl(this.surveyUrl);
    this.locale = this.locale.toLowerCase();
  }

  private reduce(data: any): any {
    const reducedData: any = {};
    Object.assign(reducedData, data);
    return reducedData;
  }

  apiReportGet(resource: string, options: any = {}): Observable<any> {
    return this.http
      .get(`${this.surveyUrl}${resource}`, options)
      .pipe(map(res => res));
  }

  apiReportBlobGet(resource: string, options: any = {}): Observable<any> {
    return this.http
      .get(`${this.surveyUrl}${resource}`, options);
  }

  apiReportPost(resource: string, body: any = {}, options: any = {}): Observable<any> {
    return this.http
      .post(`${this.surveyUrl}${resource}`, body, options)
      .pipe(map(res => res));
  }

  apiReportBlobPost(resource: string, body: any = {}, options: any = {}): Observable<any> {
    return this.http
      .post(`${this.surveyUrl}${resource}`, body, options);
  }

  hasData(): boolean {
    return this.data.hasOwnProperty('candidate');
  }

  getReport(xrefId: number, refereeId: number, success: any = () => { }, error: any = () => { }): void {
    // Take the success parsed into the function, perform local logic first then parsed success
    const successWrapped = (data: any) => {
      this.data = this.reduce(data.data);
      this.dataUpdated$.next(true);
      success(this.data);
    };

    // Take the error parsed into the function, perform local logic first then parsed success
    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    if (!this.hasData() || refereeId != this.data.referee.referee_id || xrefId != this.data.xref_id) {
      this.apiReportGet(`v1/check/${xrefId}/referee/${refereeId}`).subscribe(successWrapped, errorWrapped);
    } else {
      success(this.data);
    }
  }

  getReportPromise(xrefId: number, refereeId: number) : Observable<any> {
    return this.apiReportGet(`v1/check/${xrefId}/referee/${refereeId}`);
  }

  // @success <optional>: A success method parsed from the caller to be executed in that context
  // @error <optional>: An error method parsed from the caller to be executed in that context
  exportReport(data: any, xrefId: number, refereeId: number, checkId: number, companyName: string, success: any = () => { }, error: any = () => { }): void {
    // Take the success parsed into the function, perform local logic first then parsed success
    const successWrapped = (res: any) => {
      const company = companyName.replace(' ', '_');

      // Use FileSaver.js to perform file save
      saveAs.saveAs(res, `${company}-${checkId}.pdf`);

      success();
    };

    // Take the error parsed into the function, perform local logic first then parsed success
    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    const options = {
      responseType: 'blob'
    };

    this.apiReportPost(`xref-report/${xrefId}/referee/${refereeId}/pdf`, data, options).subscribe(successWrapped, errorWrapped);
  }

  downloadRefereeReport(checkId: number, refereeId: number, content, success: any = () => { }, error: any = () => { }): void {

    const successWrapped = (data: any) => {
      // Use FileSaver.js to perform file save
      saveAs.saveAs(data, `referee-${refereeId}-report.pdf`);

      success();
    };

    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    const options = {
      responseType: 'blob'
    };

    this.apiReportBlobGet(`xref-report/referee/${refereeId}/pdf`, options).subscribe(successWrapped, errorWrapped);
  }

  downloadRefereeAttachment(checkId: number, fileId: string, fileName: string, success: any = () => { }, error: any = () => { }): void {

    // remove hyphens from uuid, if any, as that's the format accepted by the open API URL
    if (fileId) {
        fileId = fileId.replace(/-/g, '');
    }

    const successWrapped = (data: any) => {
      // Use FileSaver.js to perform file save
      saveAs.saveAs(data, `${fileName}`);

      success();
    };

    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    const options = {
      responseType: 'blob'
    };

    this.apiReportBlobGet(`v1/check/${checkId}/attachment/${fileId}`, options).subscribe(successWrapped, errorWrapped);
  }

  apiReplaceReferee(resourceUrl: string, content: any): Observable<any> {
    return this.api
      .post(`${resourceUrl}`, content)
      .pipe(map((res: any) => res));
  }

  deprecatedShareReport(refereeId: number, options: any) {
    return this.api.post(`v1/referee/${refereeId}/replace`, options).pipe(map(data => data));
  }

  shareReport(checkId: number, refereeId: number, options: any) {
    return this.api.post(`v1/check/${checkId}/referee/${refereeId}/replace`, options).pipe(map(data => data));
  }

  getReplaceRefereeStatus(checkId: number, refereeId: number) {
    return this.apiReportGet(`v1/check/${checkId}/referee/${refereeId}/replace`).pipe(map(data => data));
  }

  deprecatedGetReplaceRefereeStatus(refereeId: number) {
    return this.apiReportGet(`v1/referee/${refereeId}/replace`).pipe(map(data => data));
  }
  apiShareReportPost(checkId: number, refereeId: number, email: any = {}): Observable<any> {
    const url = `v1/check/${checkId}/referee/${refereeId}/report/${this.locale}/share`;
    return this.http.post(`${this.surveyUrl}${url}`, email).pipe(map(data => data));
}


  addUnusualActivityNote(checkId: number, refereeId: number, note: string, options: any = {}) {
    return this.apiReportPost(`v1/check/${checkId}/referee/${refereeId}/ua/note`, {'comments': note}, options).pipe(map(data => data));
  }

  addUnusualActivityResolution(checkId: number, refereeId: number, resolution: number, options: any = {}) {
    return this.apiReportPost(`v1/check/${checkId}/referee/${refereeId}/ua/resolution`, {'resolution': resolution}, options).pipe(map(data => data));
  }

  getCompanyDomains(): Observable<HttpResponse<any>> {
    return this.apiReportGet(`v1/company/domains`).pipe(map(data => data));
  }

  deprecatedAddReplaceRefereeDetails(refereeId: number, selectedReason: number): Observable<HttpResponse<any>> {
    const content = {
      'selected_reason': selectedReason
    };
    return this.apiReportPost(`v1/referee/${refereeId}/replace/send-email`, content).pipe(map((data: any) => data));
  }

  addReplaceRefereeDetails(checkId: number, refereeId: number, selectedReason: number): Observable<HttpResponse<any>> {
    const content = {
      'selected_reason': selectedReason
    };
    return this.apiReportPost(`v1/check/${checkId}/referee/${refereeId}/replace/send-email`, content).pipe(map((data: any) => data));
  }

  exportPDF(checkId: number, refereeId: number, componentsIncluded: any = {}, success: any = () => { }, error: any = () => { }): void {
    const content = {
      'components_included': componentsIncluded
    };
    const successWrapped = (data: any) => {
      // Use FileSaver.js to perform file save
      saveAs.saveAs(data, `${checkId}.pdf`);

      success();
    };

    const errorWrapped = (err: any) => {
      error(err.statusText);
    };

    let options = {
      responseType: 'blob'
    };
    this.apiReportBlobPost(`v1/check/${checkId}/referee/${refereeId}/report/${this.locale}/pdf`, content, options).subscribe(successWrapped, errorWrapped);
  }
}
